import i18n from '@/i18n';
// NOTE: we use EN questions as base for correct "answers"
import { captureException } from '@sentry/react';
import { changeLanguage } from './../../../i18n';
import questionJsonEN from '@/assets/lang/en/questions.json';
import type { Question } from './types';

const TOTAL_QUIZZES = 2; // num of quizzes that will be played
const TOTAL_QUESTIONS = 20; // num of questions in total

export const getQuizzQuestions = async ({
  quizzIndex,
  numQuestions,
}: {
  quizzIndex: number;
  numQuestions: number;
}) => {
  try {
    const questions: Question[] = [];
    let currentLang = i18n.language;

    // security fallback to en
    if (!i18n.languages.includes(currentLang)) {
      console.warn(
        `Language ${currentLang} not found, falling back to English`
      );
      changeLanguage('en');
      currentLang = 'en';
    }

    // Import the questions file dynamically based on current language
    // Import the questions module and get the default export (the JSON object)
    const questionsModule = await import(
      `@/assets/lang/${currentLang}/questions.json`
    );
    const questionsJson: any = questionsModule.default;

    // Process the questions
    const rangeOfQuestionsForQuizz = TOTAL_QUESTIONS / TOTAL_QUIZZES;
    const startIndex = quizzIndex * rangeOfQuestionsForQuizz;
    const randomQuestionIds = Array.from(
      { length: rangeOfQuestionsForQuizz },
      (_, i) => startIndex + i
    )
      .sort(() => Math.random() - 0.5)
      .slice(0, numQuestions);

    // TODO: remove this line // OR Keep for simle debugging
    // randomQuestionIds = [19, 17, 18, 19, 4, 5, 6]

    for (let i = 0; i < numQuestions; i++) {
      const id = randomQuestionIds[i] + 1; // we add 1 because the ids start at 0 in the json, but at 1 in the questions object
      const name: `q${number}` = `q${id}`;
      const answerString: string = (questionJsonEN as any)[
        `${name}.correct`
      ].toLowerCase();

      // get num of options available in the question
      const allPossibleOptions = ['a', 'b', 'c', 'd', 'e'];
      let numOptions = 0;
      allPossibleOptions.forEach((option) => {
        if (questionsJson[`${name}.${option}`]) {
          numOptions += 1;
        }
      });

      // get all possible links for the question
      const allPossibleLinks = [
        'link_1',
        'link_2',
        'link_3',
        'link_4',
        'link_5',
      ];
      let numLinks = 0;
      allPossibleLinks.forEach((link) => {
        if (questionsJson[`${name}.${link}`]) {
          numLinks += 1;
        }
      });
      // console.log(questionsJson);
      questions.push({
        id: id,
        title: questionsJson[`${name}.title`],
        options: allPossibleOptions
          .slice(0, numOptions)
          .map((option) => questionsJson[`${name}.${option}`]),
        correctAnswer: [allPossibleOptions.indexOf(answerString)],
        explain: questionsJson[`${name}.explain`],
        links: allPossibleLinks
          .slice(0, numLinks)
          .map((link_key) => questionsJson[`${name}.${link_key}`]),
      });
    }
    return questions;
  } catch (error) {
    console.error('Error loading questions:', error);
    captureException(error);
    return [];
  }
};
